import { AxiosError } from 'axios';
import i18n from 'i18next';
// Helper functions
// Components
import {
  Button,
  CollapseCard,
  GridItem,
  notify,
  PendingContent,
  Spinner,
} from 'plume-ui';
import { CollapseCardStyles } from 'plume-ui/dist/components/Card/CollapseCard';
import { DataRow } from 'plume-ui/dist/components/Table/Table';
import React, { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { DependencyContainer } from '../../../../DependencyContainer';
import { useFetchData } from '../../../../hooks/useFetchData';
import { MixPanelEvents } from '../../../../mixPanelEvents';
import {
  activePartnerAtom,
  configurationInfoModalAtom,
  webhookModalAtom,
} from '../../../../state';
import { ConfigurationInfoModalData, Maybe } from '../../../../types';
import FormattedMessage from '../../../../utils/components/FormattedMessage'; // Hooks
import { AdjustableComponent } from '../../../../utils/hooks/useClasses';
import StickyActionTable from '../../../Monitor/components/MonitorInfo/StickyActionTable/StickyActionTable';
import { useTrackEvent } from '../../../trackingAnalytics/hooks/useTrackEvent';
import { EmptyWebhookObject, webhookListTableHeaders } from './fixtures';
import { parseWebhookToDataRows } from './helpers';
import { TestingWebhook, WebhookStatus } from './types';
import { ErrorResponse } from '../../../../types';

type WebhookProps = {
  editAllowed: boolean;
};

type WebhookStyles = {};

const { configurationService } = new DependencyContainer();

const Webhook: AdjustableComponent<WebhookProps, WebhookStyles> = ({
  editAllowed,
}) => {
  const { t } = useTranslation();
  const setModal = useSetRecoilState(webhookModalAtom);
  const setInfoModal = useSetRecoilState(configurationInfoModalAtom);
  const activePartner = useRecoilValue(activePartnerAtom);
  const [tableData, setTableData] = useState<DataRow[]>([]);
  const trackEvent = useTrackEvent();

  const [
    loadingWebhooks,
    webhookList,
    webhooksLoadingError,
    runFetchWebhooksList,
    /*webhooksFetchComplete,*/
  ] = useFetchData<WebhookStatus[], any>(
    async () => {
      if (!activePartner?.id) return;
      const listItems = await configurationService.getWebhooksListItems(
        activePartner.id,
      );
      return listItems;
    },
    undefined,
    [activePartner],
    () => activePartner === undefined,
  );

  const getWebhookById = (id: string) =>
    webhookList?.find((item) => item.id === id);

  const handleEnableDisableWebhook = async (id: string, enabled: boolean) => {
    if (!activePartner?.id) return;
    await configurationService.updateWebhook(activePartner.id, id, { enabled });
  };

  const handleAddNew = () => {
    setModal({
      isOpen: true,
      type: 'add',
      webhook: EmptyWebhookObject,
      refetchWebhooks: () => runFetchWebhooksList(),
      testWebhook: handleTestWebhook,
    });
  };

  const handleEditWebhook = (id: string) => {
    setModal({
      isOpen: true,
      type: 'edit',
      webhook: getWebhookById(id) || EmptyWebhookObject,
      refetchWebhooks: () => runFetchWebhooksList(),
      testWebhook: handleTestWebhook,
    });
  };

  const notifyDeleteError = () => {
    notify({
      title: t('configuration.notifications.error.webhook.delete.title'),
      body: t('configuration.notifications.error.webhook.delete.body'),
      type: 'error',
    });
  };

  const handleDeleteWebhook = async (id: string) => {
    if (!activePartner?.id) {
      notifyDeleteError();
      return;
    }
    await configurationService
      .deleteWebhook(activePartner.id, id)
      .then(() => {
        runFetchWebhooksList();
        notify({
          title: t('configuration.notifications.success.webhook.delete.title'),
          body: t('configuration.notifications.success.webhook.delete.body'),
          type: 'success',
        });
        trackEvent({
          eventName: MixPanelEvents.WEBHOOK_SUCCESS,
          additionalContent: {
            INTERACTION: 'DELETE',
            WEBHOOK_ID: getWebhookById(id)?.id,
          },
        });
      })
      .catch((error) => {
        notifyDeleteError();
        trackEvent({
          eventName: MixPanelEvents.WEBHOOK_FAILURE,
          additionalContent: {
            INTERACTION: 'DELETE',
            WEBHOOK_ID: getWebhookById(id)?.id,
            ERROR_MESSAGE:
              (error as AxiosError<ErrorResponse>).response?.data?.message ||
              'no message',
          },
        });
      });
  };

  useEffect(() => {
    if (webhookList === undefined) return;
    setTableData(parseWebhookToDataRows(webhookList));
  }, [loadingWebhooks]);

  const handleTestWebhook = async (webhookData: TestingWebhook | undefined) => {
    if (!activePartner?.id || webhookData === undefined) return;
    return await configurationService.testWebhook(
      activePartner?.id,
      webhookData,
    );
  };

  const tableHeaders = webhookListTableHeaders({
    webhookList: webhookList || [],
    openOutreachListModal: (data: Maybe<ConfigurationInfoModalData>) => {
      setInfoModal({
        isOpen: true,
        type: 'outreachList',
        data,
      });
    },
    openErrorDetailsModal: (data: Maybe<ConfigurationInfoModalData>) => {
      setInfoModal({
        isOpen: true,
        type: 'lastError',
        data,
      });
    },
    deleteWebhook: handleDeleteWebhook,
    editWebhook: handleEditWebhook,
    setEnabled: handleEnableDisableWebhook,
    editAllowed,
  });

  return (
    <GridItem colSpan="12" tabletColSpan="12">
      <CollapseCard
        title={
          <div className="Configuration__collapseCard__title">
            <FormattedMessage id="configuration.webhook.title" />
          </div>
        }
        subtitle={i18n.t('configuration.webhook.subtitle')}
        classes={(current: CollapseCardStyles) => ({
          ...current,
          root: `${current.root} Configuration__collapseCard`,
          content: `${current.content} Configuration__collapseCard__content`,
        })}
      >
        <PendingContent
          loading={loadingWebhooks && !webhooksLoadingError}
          loader={Spinner}
        >
          {webhookList !== undefined && Boolean(tableData.length) && (
            <StickyActionTable
              tableType="standard"
              truncateCellContent
              headerRow={tableHeaders}
              dataRows={tableData}
              onSortChange={runFetchWebhooksList}
            />
          )}
        </PendingContent>
        <Button
          styleVariant="primary"
          onClick={handleAddNew}
          disabled={!editAllowed}
        >
          <FormattedMessage id="configuration.actions.addNew" />
        </Button>
      </CollapseCard>
    </GridItem>
  );
};

export default memo(Webhook);
